import { Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { distinct, groupByToMap } from "@entrilia/shared/utilities/arrayHelper";
import type { ImpersonationContact, ImpersonationContactGroup } from "../../api/types/impersonationTypes";
import useClientContext from "../../contexts/useClientContext";
import { viewAs } from "../../impersonation/impersonationHandler";
import ImpersonationOptionsList from "./ImpersonationOptionsList";

const doContactsMatchFilterValue = (contacts: ImpersonationContact[], filterValue: string) =>
  contacts.some(
    (contact) => contact.email.toLowerCase().includes(filterValue) || contact.name.toLowerCase().includes(filterValue)
  );

const filterContactGroups = (
  contactGroups: ImpersonationContactGroup[],
  filterText: string
): ImpersonationContactGroup[] => {
  if (filterText.trim() === "") {
    return contactGroups;
  }

  const filterValue = filterText.trim().toLowerCase();

  return contactGroups.filter(
    (x) =>
      x.title.toLowerCase().includes(filterValue) ||
      (x.fundName ?? "").toLowerCase().includes(filterValue) ||
      doContactsMatchFilterValue(x.contacts, filterValue)
  );
};

interface Props {
  email: string;
  contactGroups: ImpersonationContactGroup[];
}

const ImpersonationSearchPanel = ({ email, contactGroups }: Props) => {
  const { impersonationFundName } = useClientContext();

  const contactGroupsByFundName = useMemo(
    () =>
      groupByToMap(
        contactGroups.filter((g) => Boolean(g.fundName)),
        (group) => group.fundName ?? ""
      ),
    [contactGroups]
  );

  const fundNames = useMemo(
    () => distinct(contactGroups.map((x) => x.fundName ?? "").filter(Boolean)).sort((a, b) => a.localeCompare(b)),
    [contactGroups]
  );

  const existingFundName = fundNames.includes(impersonationFundName || "") ? impersonationFundName : undefined;

  const [currentFundName, setCurrentFundName] = useState<string>(existingFundName || "");

  const [filterText, setFilterText] = useState("");

  const handleImpersonationView = (email: string) => {
    viewAs(email, currentFundName);
  };

  const handleFundChange = (fundName: string) => {
    setCurrentFundName(fundName);
    setFilterText("");
  };

  const contactGroupsOfFund = currentFundName
    ? (contactGroupsByFundName.get(currentFundName) ?? [])
    : [...contactGroups];

  const filteredContactGroups = filterContactGroups(contactGroupsOfFund, filterText);

  return (
    <>
      <Stack spacing={3}>
        <Grid container sx={{ width: "100%" }}>
          <Grid size="grow" sx={{ px: 2 }}>
            <SearchField
              initialValue={filterText}
              onSearch={(value) => setFilterText(value)}
              debounceTimeMs={300}
              fullWidth
              autoFocus
            />
          </Grid>

          {fundNames.length > 0 && (
            <Grid size={5} sx={{ px: 2 }}>
              <Select
                sx={(theme) => ({
                  width: "100%",
                  "&.MuiMenu-list": {
                    py: theme.spacing(0.5),
                  },
                })}
                onChange={(e) => handleFundChange(e.target.value)}
                value={currentFundName}
                displayEmpty
              >
                <MenuItem value="">All Funds</MenuItem>
                {fundNames.map((fund) => (
                  <MenuItem key={fund} value={fund}>
                    {fund}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
        </Grid>

        <Grid container sx={{ width: "100%", pb: 2 }}>
          <Grid size={5.5} sx={{ px: 7 }}>
            <Typography variant="subtitle1">Name</Typography>
          </Grid>

          <Grid size="grow">
            <Typography variant="subtitle1">Email</Typography>
          </Grid>
        </Grid>
      </Stack>
      <ImpersonationOptionsList
        handleImpersonationView={handleImpersonationView}
        contactEmail={email}
        filterText={filterText}
        contactGroups={filteredContactGroups}
        showFundName={!currentFundName}
      />
    </>
  );
};

export default ImpersonationSearchPanel;
